import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';

import { MiniGamesData } from '../../../store/miniGames/miniGamesActions';
import { useBonusCountdown } from '../../../utils/hooks/useBonusCountdown';
import { PAY_TO_PLAY_MIN_LEVEL } from '../Game/types';
import ArcadeGameCard from './ArcadeGameCard';
import { ArcadeGame } from './bonusGamesCatalog';

/**
 * Figma "Featured games / 3 columns" (7019:79899). The frame carries no fill and no border of its
 * own - it is a bare flex row, `gap: 8`, and the three 118-wide cards fill the 370 column exactly
 * (3 x 118 + 2 x 8). What reads as a plate around the group on the artboard is the cards' own
 * glow: each one carries `drop-shadow 0 0 10 #FF7200` outside a bright `#FFF908` hairline.
 */
export const FEATURED_DESIGN_WIDTH = 370;
export const FEATURED_DESIGN_HEIGHT = 176;
const CARD_GAP = 8;

/** The smallest stake (`MINI_GAMES_STAKES[0]` in trinity-nest), shown once the free play is used. */
export const PAID_PLAY_PRICE = 200;

type Props = {
	games: ArcadeGame[];
	width: number;
	onPlay: (game: ArcadeGame) => void;
	canPlay: (game: ArcadeGame) => boolean;
	miniGames?: MiniGamesData;
	playerLevel: number;
	/** A paid play below `PAY_TO_PLAY_MIN_LEVEL`: the backend refuses it, so the screen explains. */
	onLevelRequired: (game: ArcadeGame) => void;
};

export const FeaturedGames = ({
	games,
	width,
	onPlay,
	canPlay,
	miniGames,
	playerLevel,
	onLevelRequired,
}: Props) => {
	const scale = width / FEATURED_DESIGN_WIDTH;
	const styles = useMemo(() => createStyles(scale), [scale]);

	return (
		<View style={styles.row}>
			{games.map(game => (
				<FeaturedGameCard
					key={game.key}
					game={game}
					scale={scale}
					miniGames={miniGames}
					onPress={
						canPlay(game)
							? paid =>
									paid && playerLevel < PAY_TO_PLAY_MIN_LEVEL ? onLevelRequired(game) : onPlay(game)
							: undefined
					}
				/>
			))}
		</View>
	);
};

/**
 * Every player gets one free play per game. Once it is used the card counts down to the next one
 * and its button carries the price of a paid play.
 */
const FeaturedGameCard = ({
	game,
	scale,
	miniGames,
	onPress,
}: {
	game: ArcadeGame;
	scale: number;
	miniGames?: MiniGamesData;
	onPress?: (paid: boolean) => void;
}) => {
	const data = game.miniGameKey ? miniGames?.[game.miniGameKey] : undefined;
	const { timeText, ready } = useBonusCountdown(data?.collected_at, game.cooldownHours);
	// `is_bonus` is 1 while free tries are left; `ready` covers the minute before the backend's
	// reset job hands them back once the countdown has run out.
	const paid = !!data && data.is_bonus !== 1 && !ready;

	return (
		<ArcadeGameCard
			game={game}
			variant='featured'
			scale={scale}
			timer={paid ? timeText : undefined}
			price={paid ? PAID_PLAY_PRICE : undefined}
			onPress={onPress ? () => onPress(paid) : undefined}
		/>
	);
};

const createStyles = (scale: number) =>
	StyleSheet.create({
		row: {
			width: FEATURED_DESIGN_WIDTH * scale,
			height: FEATURED_DESIGN_HEIGHT * scale,
			flexDirection: 'row',
			alignItems: 'flex-start',
			gap: CARD_GAP * scale,
		},
	});

export default FeaturedGames;
